<template>
  <div
    v-tooltip="tooltipOptions"
    :class="{ clickable: hasBillingPermission }"
    class="container"
    @click="onOpenPlanModal"
  >
    <p class="number">
      {{ getItemCount }}
    </p>
    <div>
      <progress-ring
        class="progress-ring"
        :radius="18"
        :progress="
          getItemCount < projectItemsLimit ? getItemCount : projectItemsLimit
        "
        :progress-limit="projectItemsLimit"
        :fill-color="'transparent'"
        :base-ring-color="'#2c3233'"
        :stroke-color="strokeColor"
        :stroke="3"
      />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { useProtect } from '@/composables/useProtect.js';
import { usePremiumFeature } from '@/composables/usePremiumFeature.js';
import { EVENT, SYS_PERMISSIONS } from '@/constants.js';
import ProgressRing from '../../all/ProgressRing.vue';

export default {
  components: {
    ProgressRing,
  },

  setup() {
    const { isLimited: isProjectItemsLimited, limit: projectItemsLimit } =
      usePremiumFeature({
        feature: 'project_items',
      });

    const { hasPermission: hasBillingPermission } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_BILLING_MANAGE],
    });

    return {
      isProjectItemsLimited,
      projectItemsLimit,
      hasBillingPermission,
    };
  },

  computed: {
    ...mapGetters('project/data', ['getItemCount']),

    tooltipOptions() {
      return {
        placement: 'bottom',
        content: this.tooltipContent,
        html: true,
        distance: 0,
        delay: { show: 800, hide: 0 },
      };
    },

    /**
     * Use the default color unless we reach more than 90% of available items
     */
    strokeColor() {
      if (this.getItemCount / this.projectItemsLimit >= 0.9) {
        return '#db0f0f';
      }
      return '#db841e';
    },

    tooltipContent() {
      if (this.hasBillingPermission) {
        if (this.getItemCount / this.projectItemsLimit < 0.9) {
          return `${this.getItemCount}/${this.projectItemsLimit} items used<br/><strong>Upgrade for unlimited space</strong>`;
        }
        return `<strong>${this.getItemCount}/${this.projectItemsLimit} items used</strong><br/>Your free item limit is almost up! <br/><strong>Upgrade for unlimited space</strong>`;
      }
      return `${this.getItemCount}/${this.projectItemsLimit} items used<br/>`;
    },
  },

  methods: {
    onOpenPlanModal() {
      if (this.hasBillingPermission) {
        // TODO: Add message
        emitter.emit(EVENT.LIMIT_MODAL, {
          open: true,
          title: `${this.getItemCount}/${this.projectItemsLimit} items used`,
          subtitle:
            'Upgrade to any of our plans to unlock unlimited project items',
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.container {
  height: 100%;
  align-items: center;
  position: relative;
  display: flex;

  &.clickable,
  &.clickable .number {
    cursor: pointer !important;
  }
  > div {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
  }

  font-size: 12px;
  .number {
    position: absolute;
    color: #fff;
    text-align: center;
    width: 100%;
    font-size: 10px;
    font-weight: 600;
    cursor: default;
    user-select: none;
  }
  .progress-ring {
    margin: 0 10px;
  }

  .progress {
    -moz-appearance: none;
    -webkit-appearance: none;
    border: none;
    border-radius: 3px;
    height: 12px;
    overflow: hidden;
    width: 30px;
    background: #252929;
    margin: 0 15px;

    &.full {
      &::-webkit-progress-value {
        background: #ec3921 !important;
      }
    }
  }
}
</style>
